/**
 * Voice Feature Detection
 *
 * Checks browser support for Web Speech API features.
 */

// Type definitions for Web Speech API (not all browsers have these in lib.dom.d.ts)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SpeechRecognitionConstructor = new () => any;

interface WindowWithSpeechRecognition extends Window {
  SpeechRecognition?: SpeechRecognitionConstructor;
  webkitSpeechRecognition?: SpeechRecognitionConstructor;
}

/**
 * Voice feature support status
 */
export interface VoiceSupport {
  /** SpeechRecognition API available */
  recognition: boolean;
  /** SpeechSynthesis API available */
  synthesis: boolean;
  /** Either feature available */
  any: boolean;
  /** Both features available */
  full: boolean;
}

// Browser prefixed SpeechRecognition
const SpeechRecognitionImpl: SpeechRecognitionConstructor | undefined =
  (window as WindowWithSpeechRecognition).SpeechRecognition ||
  (window as WindowWithSpeechRecognition).webkitSpeechRecognition;

/**
 * Check if SpeechRecognition API is available
 */
export function isSpeechRecognitionSupported(): boolean {
  return typeof SpeechRecognitionImpl !== 'undefined';
}

/**
 * Check if SpeechSynthesis API is available
 */
export function isSpeechSynthesisSupported(): boolean {
  return 'speechSynthesis' in window && typeof window.speechSynthesis !== 'undefined';
}

/**
 * Get complete voice support status
 */
export function getVoiceSupport(): VoiceSupport {
  const recognition = isSpeechRecognitionSupported();
  const synthesis = isSpeechSynthesisSupported();

  return {
    recognition,
    synthesis,
    any: recognition || synthesis,
    full: recognition && synthesis,
  };
}

/**
 * Get the SpeechRecognition constructor (handles browser prefixes)
 */
export function getSpeechRecognitionConstructor(): SpeechRecognitionConstructor | null {
  if (!isSpeechRecognitionSupported()) {
    return null;
  }
  return SpeechRecognitionImpl ?? null;
}
